/**
 * useMissao — snapshot visual do Mission Control para UMA execução.
 *
 * Transporte: as queries que já existem — `GET /executions/{id}/trace` (eventos da
 * timeline com `seq` monotônico + status da execução no banco; polling de 5 s
 * enquanto não termina, o mesmo do Mission Control) e, com a execução concluída,
 * `GET /executions/{id}/delivery-status` (entrega pendente → PR).
 *
 * Ordenação e isolamento: um store por execução (`criarStoreDaMissao`). Resposta
 * com `seq` menor que o último aplicado é ignorada (não regride a tela); trocar de
 * execução faz `reset` explícito — eventos de uma execução nunca alteram outra.
 * Desconexão: se a consulta falhar depois de já ter dados, mantém o último
 * snapshot válido e informa `conexao: 'perdida'` (a UI para a animação).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useExecutionTrace, useDeliveryStatus } from '@/api/executions';
import { criarStoreDaMissao, derivarMissao, estadoVisual } from './missionModel';

const TERMINAL = new Set(['done', 'completed', 'failed', 'cancelled', 'sla_expired']);

export function useMissao(executionId, { activeAgents = null } = {}) {
  const poll = useMemo(
    () => (q) => (TERMINAL.has(String(q.state.data?.execution?.status ?? '').toLowerCase()) ? false : 5000),
    [],
  );
  const trace = useExecutionTrace(executionId, { refetchInterval: poll });
  const execStatus = trace.data?.execution?.status ?? null;
  const concluida = estadoVisual(execStatus) === 'completed';
  const delivery = useDeliveryStatus(concluida ? executionId : null, {
    refetchInterval: (q) => ((Number(q.state.data?.awaiting_approval) || 0) > 0 ? 15000 : false),
  });

  const derivado = useMemo(() => {
    if (!executionId || !trace.data) return null;
    return derivarMissao({
      executionId,
      execStatus,
      events: trace.data.events,
      delivery: delivery.data ?? null,
      activeAgents,
    });
  }, [executionId, trace.data, execStatus, delivery.data, activeAgents]);

  const storeRef = useRef(null);
  const [snap, setSnap] = useState(null);

  // Troca de execução: descarta o store anterior (reset explícito).
  useEffect(() => {
    storeRef.current = null;
    setSnap(null);
  }, [executionId]);

  useEffect(() => {
    if (!derivado) return;
    const st = storeRef.current;
    if (!st || st.get().runId !== derivado.runId) {
      storeRef.current = criarStoreDaMissao(derivado);
      setSnap(derivado);
    } else if (st.apply(derivado)) {
      setSnap(st.get());
    }
  }, [derivado]);

  const snapshot = snap && snap.runId === executionId ? snap : null;
  let conexao = 'ok';
  if (!executionId) conexao = 'ok';
  else if (trace.isError) conexao = snapshot ? 'perdida' : 'erro';
  else if (trace.isLoading) conexao = 'carregando';

  return {
    snapshot,
    conexao,
    erro: trace.error ?? null,
    projeto: trace.data?.execution?.project_name ?? null,
    refetch: () => trace.refetch(),
  };
}
